import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, CheckCircle2, Loader2, ChevronDown, ChevronUp, Camera } from 'lucide-react';
import { trackFunnelEvent } from '@/utils/tracking';

const STORAGE_KEY = 'powersure_quote_contact';

// Categories that show optional photo uploads
const PHOTO_UPLOAD_CATEGORIES = ['solar', 'heat_pumps', 'ev_charger', 'batteries'];
// Categories where meterkast photo is REQUIRED
const METERKAST_REQUIRED = ['batteries', 'ev_charger', 'solar'];
// Categories where situation photo is REQUIRED (with category-specific label)
const SITUATION_PHOTO_REQUIRED = ['solar', 'ev_charger'];

const SITUATION_LABELS = {
  nl: {
    solar: 'Foto van het dak / dakoppervlak *',
    ev_charger: 'Foto van de plek waar de laadpaal moet komen *',
  },
  en: {
    solar: 'Photo of the roof surface *',
    ev_charger: 'Photo of the intended charger location *',
  },
};

const SITUATION_ERRORS = {
  nl: {
    solar: 'Voeg een foto van het dakoppervlak toe.',
    ev_charger: 'Voeg een foto toe van de plek waar de laadpaal moet komen.',
  },
  en: {
    solar: 'Please add a photo of the roof surface.',
    ev_charger: 'Please add a photo of the intended charger location.',
  },
};

const CTA_LABELS = {
  nl: {
    insulation: { label: 'Offerte aanvragen', description: 'Powersure helpt u aan geschikte isolatiebedrijven in uw regio.' },
    heat_pumps: { label: 'Offerte aanvragen', description: 'Powersure helpt u aan geschikte warmtepomp-installateurs in uw regio.' },
    boilers: { label: 'Offerte aanvragen', description: 'Powersure helpt u aan geschikte verwarmingsspecialisten in uw regio.' },
    ventilation: { label: 'Offerte aanvragen', description: 'Powersure helpt u aan geschikte ventilatie-installateurs in uw regio.' },
    glazing: { label: 'Offerte aanvragen', description: 'Powersure helpt u aan geschikte beglazingsspecialisten in uw regio.' },
    solar: { label: 'Offerte aanvragen', description: 'Powersure helpt u aan geschikte zonnepanelen-installateurs in uw regio.' },
    batteries: { label: 'Offerte aanvragen', description: 'Powersure helpt u aan geschikte thuisbatterij-specialisten in uw regio.' },
    ev_charger: { label: 'Offerte aanvragen', description: 'Vraag vrijblijvend een laadpaalofferte aan. Powersure helpt u deze aanvraag beter te laten verlopen en matcht uw aanvraag waar mogelijk met geschikte uitvoerende partijen.' },
  },
  en: {
    insulation: { label: 'Request quote', description: 'Powersure will help match you with qualified insulation installers.' },
    heat_pumps: { label: 'Request quote', description: 'Powersure will help match you with qualified heat pump installers.' },
    boilers: { label: 'Request quote', description: 'Powersure will help match you with qualified heating specialists.' },
    ventilation: { label: 'Request quote', description: 'Powersure will help match you with qualified ventilation installers.' },
    glazing: { label: 'Request quote', description: 'Powersure will help match you with qualified glazing installers.' },
    solar: { label: 'Request quote', description: 'Powersure will help match you with qualified solar installers.' },
    batteries: { label: 'Request quote', description: 'Powersure will help match you with qualified battery installers.' },
    ev_charger: { label: 'Request quote', description: 'Request a charger quote. Powersure will match your request with suitable installation partners.' },
  },
};

const CATEGORY_CONTEXT = {
  nl: {
    insulation: 'isolatie',
    heat_pumps: 'warmtepomp',
    boilers: 'verwarmingsketel',
    ventilation: 'ventilatie',
    glazing: 'beglazing / HR++ glas',
    solar: 'zonnepanelen',
    batteries: 'thuisbatterij',
    ev_charger: 'laadpaal thuis',
  },
  en: {
    insulation: 'insulation',
    heat_pumps: 'heat pump',
    boilers: 'heating boiler',
    ventilation: 'ventilation',
    glazing: 'HR++ glazing',
    solar: 'solar panels',
    batteries: 'home battery',
    ev_charger: 'home EV charger',
  },
};

const HOME_TYPE_NL = {
  'Detached': 'Vrijstaande woning', 'Detached House': 'Vrijstaande woning',
  'Semi-detached': 'Twee-onder-één-kap', 'Semi-detached House': 'Twee-onder-één-kap', 'Semi-Detached House': 'Twee-onder-één-kap',
  'Terraced': 'Rijtjeshuis', 'Terraced House': 'Rijtjeshuis', 'Row house': 'Rijtjeshuis',
  'Apartment': 'Appartement', 'Corner house': 'Hoekwoning', 'Bungalow': 'Bungalow',
  'Monument': 'Monument', 'Monument / Protected': 'Monument', 'Farmhouse': 'Boerderij',
};

const BUILD_PERIOD_NL = {
  'Before 1930': 'Voor 1930', 'Before 1975': 'Voor 1975', 'After 2015': 'Na 2015',
};

function generateContext(category, report, lang) {
  const ctx = (CATEGORY_CONTEXT[lang] || CATEGORY_CONTEXT.en)[category] || category;
  const hp = report?.home_profile || {};
  const rawType = hp.home_type || '';
  const rawPeriod = hp.build_period || '';
  const size = hp.size || '';
  const homeType = lang === 'nl' ? (HOME_TYPE_NL[rawType] || rawType) : rawType;
  const buildPeriod = lang === 'nl' ? (BUILD_PERIOD_NL[rawPeriod] || rawPeriod) : rawPeriod;
  if (lang === 'nl') {
    return `Ik ben geïnteresseerd in ${ctx} voor mijn woning (${homeType}, bouwjaar ${buildPeriod}, ${size}).`;
  }
  return `I'm interested in ${ctx} for my home (${homeType}, built ${buildPeriod}, ${size}).`;
}

function loadSavedContact() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || null;
  } catch { return null; }
}

function saveContact(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

export default function QuoteRequestCTA({ category, reportId, country, region, postcode, email, report, chargerTerrain }) {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'en';

  const saved = loadSavedContact();
  const hasSaved = !!saved;

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(!hasSaved);
  const [showLayer2, setShowLayer2] = useState(false);
  const [terrainAnswer, setTerrainAnswer] = useState(chargerTerrain || '');

  // Layer 1 fields
  const [name, setName] = useState(saved?.name || '');
  const [contactEmail, setContactEmail] = useState(saved?.email || email || '');
  const [phone, setPhone] = useState(saved?.phone || '');
  const [contactPostcode, setContactPostcode] = useState(saved?.postcode || postcode || '');
  const [houseNumber, setHouseNumber] = useState(saved?.houseNumber || '');
  const [consent, setConsent] = useState(saved?.consent || false);

  // Layer 2 fields (optional follow-up)
  const [street, setStreet] = useState(saved?.street || '');
  const [city, setCity] = useState(saved?.city || '');
  const [extraNotes, setExtraNotes] = useState('');

  const [errors, setErrors] = useState({});

  // Photo uploads (only for relevant categories)
  const showPhotoUpload = PHOTO_UPLOAD_CATEGORIES.includes(category);
  const [meterkastFile, setMeterkastFile] = useState(null);
  const [situatieFile, setSituatieFile] = useState(null);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const meterkastRef = useRef(null);
  const situatieRef = useRef(null);

  useEffect(() => {
    setExtraNotes(generateContext(category, report, lang));
  }, [category, report, lang]);

  const labels = CTA_LABELS[lang] || CTA_LABELS.en;
  const cta = labels[category];

  // A-Z Production Acceptance Sprint (Feb 2026) — fire `quote_started`
  // exactly ONCE per category per page-mount when the form is presented.
  // Idempotent per session+category via a ref-guard. No PII. Hook MUST
  // sit AFTER `const cta = ...` because `cta` appears in the dep array.
  const startedRef = useRef(false);
  useEffect(() => {
    if (!cta || startedRef.current) return;
    startedRef.current = true;
    trackFunnelEvent('quote_started', {
      report_id: reportId,
      meta: {
        category,
        report_type: 'powersure_report',
        prefilled_from_storage: hasSaved,
      },
    });
  }, [category, cta, reportId, hasSaved]);

  if (!cta) return null;

  const t = lang === 'nl' ? {
    submit: 'Offerteaanvraag versturen',
    successTitle: 'Offerteaanvraag verstuurd',
    successDesc: 'Wij matchen u met geschikte vakmensen in uw omgeving.',
    name: 'Naam',
    email: 'E-mailadres',
    phone: 'Telefoonnummer',
    postcode: 'Postcode',
    houseNumber: 'Huisnr.',
    consent: 'Ik geef toestemming om gecontacteerd te worden voor deze offerte',
    editDetails: 'Gegevens aanpassen',
    savedAs: 'Aanvraag namens',
    moreDetails: 'Adresgegevens toevoegen',
    hideDetails: 'Verbergen',
    street: 'Straat',
    city: 'Plaats',
    extraNotes: 'Extra toelichting (optioneel)',
  } : {
    submit: 'Submit quote request',
    successTitle: 'Quote request submitted',
    successDesc: 'We\'ll match you with suitable professionals in your area.',
    name: 'Name',
    email: 'Email address',
    phone: 'Phone number',
    postcode: 'Postcode',
    houseNumber: 'House nr.',
    consent: 'I consent to being contacted regarding this quote',
    editDetails: 'Edit details',
    savedAs: 'Request as',
    moreDetails: 'Add address details',
    hideDetails: 'Hide',
    street: 'Street',
    city: 'City',
    extraNotes: 'Additional notes (optional)',
  };

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = true;
    if (!contactEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) e.email = true;
    if (!contactPostcode.trim()) e.postcode = true;
    if (!houseNumber.trim()) e.houseNumber = true;
    if (!street.trim()) e.street = true;
    if (!city.trim()) e.city = true;
    if (METERKAST_REQUIRED.includes(category) && !meterkastFile) e.meterkast = true;
    if (SITUATION_PHOTO_REQUIRED.includes(category) && !situatieFile) e.situatie = true;
    if (!consent) e.consent = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      saveContact({ name, email: contactEmail, phone, postcode: contactPostcode, houseNumber, consent: true, street, city });
      const resp = await fetch(`/api/quote-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          report_id: reportId,
          category,
          region: region || '',
          postcode: contactPostcode,
          house_number: houseNumber,
          country: country || 'NL',
          email: contactEmail,
          name,
          phone,
          street: street || '',
          city: city || '',
          context: extraNotes,
          consent: true,
          ev_terrain: category === 'ev_charger' ? terrainAnswer : undefined,
        }),
      });
      const result = await resp.json().catch(() => ({}));
      const reqId = result.request_id || '';

      // Upload photos if provided
      if (reqId && (meterkastFile || situatieFile)) {
        setUploadingPhotos(true);
        for (const [file, type] of [[meterkastFile, 'meterkast'], [situatieFile, 'situatie']]) {
          if (!file) continue;
          const fd = new FormData();
          fd.append('file', file);
          fd.append('quote_request_id', reqId);
          fd.append('upload_type', type);
          await fetch(`/api/quote-upload`, { method: 'POST', body: fd }).catch(() => {});
        }
        setUploadingPhotos(false);
      }

      setSubmitted(true);
      // A-Z Production Acceptance Sprint (Feb 2026) — canonical quote events.
      // `quote_submitted` is the macro funnel marker; legacy `quote_request`
      // pwTrack kept for backwards-compat (no double-count, different bus).
      trackFunnelEvent('quote_submitted', {
        report_id: reportId,
        meta: {
          category,
          region: region || '',
          request_id_present: Boolean(reqId),
          report_type: 'powersure_report',
          // No PII: name/email/phone/postcode NEVER in funnel payload.
        },
      });
      if (window.pwTrack) window.pwTrack('quote_request', { category, report_id: reportId });
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="mt-3 pt-3 border-t border-border/50" data-testid={`quote-cta-${category}`}>
        <div className="flex items-center gap-2 text-green-700 bg-green-50 rounded-lg px-4 py-3">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium">{t.successTitle}</p>
            <p className="text-xs text-green-600">{t.successDesc}</p>
          </div>
        </div>
      </div>
    );
  }

  const inputCls = (field) =>
    `w-full text-xs border rounded px-2.5 py-2 bg-white transition-colors ${errors[field] ? 'border-red-400 bg-red-50/30' : 'border-border'}`;

  return (
    <div className="mt-3 pt-3 border-t border-primary/10" data-testid={`quote-cta-${category}`}>
      <div className="bg-primary/5 rounded-lg px-4 py-3">
        <p className="text-sm font-medium text-primary">{cta.label}</p>
        <p className="text-xs text-muted-foreground mt-0.5 mb-3">{cta.description}</p>

        {/* Saved address — always visible when saved, with edit option */}
        {hasSaved && !expanded && (
          <div className="mb-3 bg-white rounded-lg px-3 py-2.5 border border-border/50">
            <div className="flex items-start justify-between">
              <div className="text-xs text-foreground leading-relaxed">
                <p className="font-medium">{saved.name}</p>
                <p className="text-muted-foreground">{saved.street} {saved.houseNumber}</p>
                <p className="text-muted-foreground">{saved.postcode} {saved.city}</p>
                {saved.email && <p className="text-muted-foreground mt-0.5">{saved.email}</p>}
              </div>
              <button onClick={() => setExpanded(true)} className="text-[10px] text-primary hover:underline flex items-center gap-0.5 flex-shrink-0 mt-0.5" data-testid={`quote-edit-${category}`}>
                {t.editDetails} <ChevronDown className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}

        {/* Layer 1: Core contact + address fields */}
        {(expanded || !hasSaved) && (
          <div className="space-y-2 mb-3">
            <div className="grid grid-cols-2 gap-2">
              <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder={t.name + ' *'} className={inputCls('name')} data-testid={`quote-name-${category}`} />
              <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder={t.email + ' *'} className={inputCls('email')} data-testid={`quote-email-${category}`} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input type="text" value={street} onChange={e => setStreet(e.target.value)} placeholder={t.street + ' *'} className={inputCls('street')} data-testid={`quote-street-${category}`} />
              <input type="text" value={houseNumber} onChange={e => setHouseNumber(e.target.value)} placeholder={t.houseNumber + ' *'} className={inputCls('houseNumber')} data-testid={`quote-housenr-${category}`} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input type="text" value={contactPostcode} onChange={e => setContactPostcode(e.target.value)} placeholder={t.postcode + ' *'} className={inputCls('postcode')} data-testid={`quote-postcode-${category}`} />
              <input type="text" value={city} onChange={e => setCity(e.target.value)} placeholder={t.city + ' *'} className={inputCls('city')} data-testid={`quote-city-${category}`} />
            </div>
            <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder={lang === 'nl' ? 'Telefoonnummer (optioneel)' : 'Phone (optional)'} className="w-full text-xs border border-border rounded px-2.5 py-2 bg-white" data-testid={`quote-phone-${category}`} />
            {hasSaved && (
              <button onClick={() => setExpanded(false)} className="text-[10px] text-muted-foreground hover:text-primary flex items-center gap-0.5">
                <ChevronUp className="w-3 h-3" /> {lang === 'nl' ? 'Inklappen' : 'Collapse'}
              </button>
            )}
          </div>
        )}

        {/* Extra terrain question for EV charger */}
        {category === 'ev_charger' && (
          <div className="mb-3">
            <p className="text-xs font-medium text-foreground mb-2">{lang === 'nl' ? 'Is laden op eigen terrein mogelijk?' : 'Is charging on own terrain possible?'}</p>
            <div className="grid grid-cols-2 gap-1.5">
              {[
                { value: 'Ja, via een eigen oprit', label: lang === 'nl' ? 'Ja, via een eigen oprit' : 'Yes, via own driveway' },
                { value: 'Ja, via een carport', label: lang === 'nl' ? 'Ja, via een carport' : 'Yes, via carport' },
                { value: 'Nee', label: lang === 'nl' ? 'Nee' : 'No' },
                { value: 'Weet ik niet zeker', label: lang === 'nl' ? 'Weet ik niet zeker' : 'Not sure' },
              ].map(opt => (
                <button key={opt.value} type="button"
                  onClick={() => setTerrainAnswer(opt.value)}
                  className={`text-[11px] px-2.5 py-1.5 rounded border transition-colors text-left ${terrainAnswer === opt.value ? 'bg-primary/10 border-primary text-primary font-medium' : 'border-border text-muted-foreground hover:border-primary/50'}`}
                  data-testid={`terrain-${opt.value.replace(/\s/g, '-')}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Layer 2: Extra notes (collapsible) */}
        <div className="mb-2">
          <button
            onClick={() => setShowLayer2(!showLayer2)}
            className="text-[10px] text-primary hover:underline flex items-center gap-0.5 mb-1"
            data-testid={`quote-layer2-toggle-${category}`}
          >
            {showLayer2 ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {showLayer2 ? t.hideDetails : (lang === 'nl' ? 'Extra toelichting toevoegen' : 'Add extra notes')}
          </button>
          {showLayer2 && (
            <div className="space-y-2 mt-1">
              <textarea
                value={extraNotes}
                onChange={e => setExtraNotes(e.target.value)}
                rows={2}
                placeholder={t.extraNotes}
                className="w-full text-xs border border-border rounded px-2.5 py-2 bg-white resize-none"
                data-testid={`quote-notes-${category}`}
              />
            </div>
          )}
        </div>

        {/* H: Intake helper text */}
        <p className="text-[10px] text-muted-foreground mb-2 italic">
          {lang === 'nl' ? 'We gebruiken uw intake om de aanvraag beter te laten verlopen.' : 'We use your intake to improve the quote request process.'}
        </p>

        {/* Photo uploads — required for technical categories */}
        {showPhotoUpload && (
          <div className="mb-3 space-y-2">
            <p className="text-[10px] text-muted-foreground italic mb-1">
              {lang === 'nl' ? 'Dit doen wij om extra offertevragen te verminderen en de aanvraag beter te laten verlopen.' : 'This helps reduce follow-up questions and improve the quote process.'}
            </p>
            <div>
              <button type="button" onClick={() => meterkastRef.current?.click()}
                className={`flex items-center gap-1.5 text-[11px] cursor-pointer ${errors.meterkast ? 'text-red-600' : 'text-primary hover:underline'}`}
                data-testid={`quote-photo-meterkast-${category}`}>
                <Camera className="w-3.5 h-3.5" />
                {lang === 'nl' ? 'Foto van de meterkast' : 'Photo of the meter cabinet'}
                {METERKAST_REQUIRED.includes(category) ? ' *' : ` (${lang === 'nl' ? 'optioneel' : 'optional'})`}
              </button>
              {METERKAST_REQUIRED.includes(category) && (
                <p className="text-[10px] text-muted-foreground ml-5 mt-0.5">
                  {lang === 'nl' ? 'Voeg een totaaloverzicht van de meterkast toe én een duidelijke foto van alle groepen.' : 'Include an overview of the meter cabinet and a clear photo of all circuit groups.'}
                </p>
              )}
              {meterkastFile && <span className="text-[10px] text-green-700 ml-5">{meterkastFile.name}</span>}
              {errors.meterkast && <span className="text-[10px] text-red-500 ml-5">{lang === 'nl' ? 'Voeg een foto van de meterkast toe.' : 'Please add a meter cabinet photo.'}</span>}
              <input ref={meterkastRef} type="file" accept="image/*" className="hidden" onChange={e => setMeterkastFile(e.target.files?.[0] || null)} />
            </div>
            <div>
              <button type="button" onClick={() => situatieRef.current?.click()}
                className={`flex items-center gap-1.5 text-[11px] cursor-pointer ${errors.situatie ? 'text-red-600' : 'text-primary hover:underline'}`}
                data-testid={`quote-photo-situatie-${category}`}>
                <Camera className="w-3.5 h-3.5" />
                {SITUATION_PHOTO_REQUIRED.includes(category)
                  ? ((SITUATION_LABELS[lang] || SITUATION_LABELS.nl)[category] || (lang === 'nl' ? 'Foto van de situatie *' : 'Situation photo *'))
                  : (lang === 'nl' ? 'Foto van de huidige situatie (optioneel)' : 'Photo of the current situation (optional)')
                }
              </button>
              {situatieFile && <span className="text-[10px] text-green-700 ml-5">{situatieFile.name}</span>}
              {errors.situatie && <span className="text-[10px] text-red-500 ml-5">{(SITUATION_ERRORS[lang] || SITUATION_ERRORS.nl)[category] || (lang === 'nl' ? 'Voeg een foto toe.' : 'Please add a photo.')}</span>}
              <input ref={situatieRef} type="file" accept="image/*" className="hidden" onChange={e => setSituatieFile(e.target.files?.[0] || null)} />
            </div>
            <p className="text-[10px] text-muted-foreground ml-5">
              {lang === 'nl' ? 'De uitvoerende partij kan hiermee direct een serieuze inschatting maken.' : 'Helps the executor make a serious initial assessment.'}
            </p>
          </div>
        )}

        {/* Consent */}
        <label className={`flex items-start gap-2 mb-3 cursor-pointer ${errors.consent ? 'text-red-600' : ''}`}>
          <input
            type="checkbox"
            checked={consent}
            onChange={e => setConsent(e.target.checked)}
            className="mt-0.5 accent-primary"
            data-testid={`quote-consent-${category}`}
          />
          <span className="text-[11px] text-muted-foreground leading-tight">{t.consent}</span>
        </label>

        <button
          onClick={handleSubmit}
          disabled={loading}
          className="w-full px-4 py-2.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2 font-medium transition-colors"
          data-testid={`quote-submit-${category}`}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {t.submit}
        </button>
      </div>
    </div>
  );
}
